import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { EmailIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z, ZodType } from 'zod'

import { login } from '../../../api/auth/login'
import { Paths } from '../../../lib/constants/path'
import { useSelectedEnrollment } from '../../../lib/contexts/selected-enrollment'
import useAuth from '../../../lib/hooks/use-auth'
import useTranslate from '../../../lib/hooks/use-translate'
import { LoginInput } from '../../../lib/types/models/auth/login'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { FormControl, FormField, FormItem, TranslatedForm } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import { getUserDefaultRedirectionPath } from './utils'

const formSchema = (language: AvailableLanguages): ZodType<LoginInput> =>
  z.object({
    email: createStringSchema('email', { isEmail: true, language }),
    password: createStringSchema('password', { minimumLength: 6, language })
  })

export const LoginForm = () => {
  const { setUser } = useAuth()
  const device = useBreakPoint()
  const { navigate } = useNavigator()
  const isMobile = device === 'mobile'
  const { t, currentLanguage } = useTranslate()
  const { selectedEnrollmentId } = useSelectedEnrollment()

  const { mutate, isPending } = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      setUser(data)
      navigate(getUserDefaultRedirectionPath(data, selectedEnrollmentId))
    },
    onError: showAxiosError
  })

  const form = useForm<LoginInput>({
    resolver: zodResolver(formSchema(currentLanguage)),
    defaultValues: { email: '', password: '' },
    mode: FORM_MODE
  })

  function onSubmit(values: LoginInput) {
    mutate(values)
  }

  return (
    <TranslatedForm<LoginInput> form={form}>
      <form onSubmit={form.handleSubmit(onSubmit)} autoComplete={'off'}>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput
                  {...field}
                  wrapperClassName="bg-white"
                  type="email"
                  placeholder="E-mail"
                  label={!isMobile ? 'E-mail' : undefined}
                  icon={EmailIcon}
                  autoComplete={'new-email'}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="h-5" />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput
                  wrapperClassName="bg-white"
                  {...field}
                  type="password"
                  label={!isMobile ? t('password.placeholder') : undefined}
                  placeholder={t('password.placeholder')}
                  autoComplete={'new-password'}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="h-2" />

        <Typography
          variant="label"
          className="underline cursor-pointer w-fit"
          color={isMobile ? tailwindColors.white.DEFAULT : tailwindColors.primary.DEFAULT}
        >
          <Link to={Paths.AUTH_FORGOT_PASSWORD_PAGE}>{t('forgot.password')}</Link>
        </Typography>

        <Button
          loading={isPending}
          type="submit"
          label={t('staff.personal.space.connect')}
          className={'w-full mt-10 mobile:bg-primary-dark-hover '}
        />
      </form>
    </TranslatedForm>
  )
}
